import { useEffect, useMemo, useState, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Input } from '@wartek-id/input'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleIcon,
} from '@wartek-id/collapsible-v1'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { useToast } from '@wartek-id/toast-v2'

import { LangkahObservasiPraktikKinerjaSection } from 'app/KinerjaShared/components/LangkahObservasiPraktikKinerjaSection'
import { assetUrl } from 'utils/url'
import { PAMONG_DUKUNGAN_UPAYA_TINDAK_LANJUT } from 'app/KinerjaPamongPage/constants/praktik-kinerja'
import { API_PATH_PELAKSANAAN_PROGRESS } from 'api/kinerja/useAPIKinerja'
import { Header } from 'app/KinerjaShared/components/Header'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
  SectionCardFooter,
} from 'app/KinerjaShared/components/SectionCard'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import DukunganTindakLanjutSelect from 'app/KinerjaShared/components/DukunganTindakLanjutSelect'
import { generateErrorMessage, getErrorCode } from 'app/KinerjaShared/utils/api'
import { PRAKTIK_KINERJA_STEPS } from 'app/KinerjaPamongPage/constants/praktik-kinerja-steps'
import { PageGuardPamongPelaksanaanStatus } from 'app/KinerjaPamongPage/components/pelaksanaan/PageGuardPamongPelaksanaanStatus'
import { HasilObservasiPraktikKinerjaPegawaiTable } from 'app/KinerjaShared/components/HasilObservasiPraktikKinerjaPegawaiTable'
import {
  API_GET_RENCANA_TINDAK_LANJUT_QUERY_KEY,
  useApiGetRencanaTindakLanjut,
  useApiUpsertRencanaTindakLanjut,
} from 'api/kinerja/spnf/pamong/pelaksanaan'
import { ConfirmationDialog } from 'app/KinerjaShared/components/ConfirmationDialog'
import { useBackNavigationConfirmation } from 'app/KinerjaShared/hooks/useBackNavigationConfirmation'

import type { CustomError } from 'utils/customError'
import type { MultiPeriodPayload } from 'types/kinerja'
import type { TStatePelaksanaan } from 'types/kinerja-pelaksanaan'
import type { ParamsUpsertRencanaTindakLanjut } from 'types/kinerja-pamong'

type RencanaTindakLanjutProps = {
  periodParams?: MultiPeriodPayload
  isReadOnly?: boolean
}

const STEP: TStatePelaksanaan = 'DOCUMENT_FOLLOW_UP'
const stepTitle = PRAKTIK_KINERJA_STEPS[STEP].title

export type FormData = ParamsUpsertRencanaTindakLanjut['detail']

export function RencanaTindakLanjut(props: RencanaTindakLanjutProps) {
  const { periodNo, periodYear } = usePeriodContext()
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()
  const upsertMutation = useApiUpsertRencanaTindakLanjut()

  // -----------------------------------------
  // Handle redirect to Praktik Kinerja Hub page
  // -----------------------------------------
  const praktikKinerjaHubRoute = `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`

  // Uses router.replace() directly to bypass beforePopState guard
  const handleGoToPraktikKinerjaHub = useCallback(() => {
    router.replace(praktikKinerjaHubRoute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [praktikKinerjaHubRoute])

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (props.periodParams) return props.periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [props.periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Handle hasil observasi dialog
  // -----------------------------------------
  const [isHasilObservasiDialogOpen, setHasilObservasiDialogOpen] =
    useState(false)
  const handleOpenHasilObservasiDialog = () => {
    setHasilObservasiDialogOpen(true)
  }
  const handleCloseHasilObservasiDialog = () => {
    setHasilObservasiDialogOpen(false)
  }

  // -----------------------------------------
  // Handle panduan pengisian refleksi collapsible
  // -----------------------------------------
  const [isPanduanExpanded, setIsPanduanExpanded] = useState(false)
  const handlePanduanToggle = () => {
    setIsPanduanExpanded(!isPanduanExpanded)
  }

  // -----------------------------------------
  // Handle form
  // -----------------------------------------
  const formMethods = useForm<FormData>({})

  // -----------------------------------------
  // Get Rencana Tindak Lanjut data
  // -----------------------------------------
  const {
    isLoading: isRencanaTindakLanjutLoading,
    data: rencanaTindakLanjutData,
  } = useApiGetRencanaTindakLanjut({
    params: {
      skpYear: resolvedPeriodParams.skpYear,
      skpPeriod: resolvedPeriodParams.skpPeriod,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // -----------------------------------------
  // Sync form with data whenever it changes
  // -----------------------------------------
  useEffect(() => {
    if (rencanaTindakLanjutData?.data?.detail) {
      formMethods.reset({
        selfReflection: rencanaTindakLanjutData.data.detail.selfReflection,
        additionalFollowUpTarget:
          rencanaTindakLanjutData.data.detail.additionalFollowUpTarget,
        additionalFollowUpEffort:
          rencanaTindakLanjutData.data.detail.additionalFollowUpEffort,
        additionalFollowUpPeriod:
          rencanaTindakLanjutData.data.detail.additionalFollowUpPeriod,
        additionalSupportNeed:
          rencanaTindakLanjutData.data.detail.additionalSupportNeed || null,
      })
    }
  }, [rencanaTindakLanjutData, formMethods])

  // -----------------------------------------
  // Resolve read only status
  // -----------------------------------------
  const isReadOnly = useMemo(() => {
    const isSubmitted = rencanaTindakLanjutData?.data?.state === 'SUBMITTED'
    return props.isReadOnly || isSubmitted
  }, [props.isReadOnly, rencanaTindakLanjutData])

  // -----------------------------------------
  // Handle save draft
  // -----------------------------------------
  const [isSaveDraftDialogOpen, setSaveDraftDialogOpen] = useState(false)

  const handleOpenSaveDraftDialog = () => {
    setSaveDraftDialogOpen(true)
  }
  const handleCloseSaveDraftDialog = () => {
    setSaveDraftDialogOpen(false)
  }
  const handleSaveDraft = () => {
    if (isReadOnly) return

    upsertMutation.mutate(
      {
        tipe: 'SPNF',
        state: 'DRAFT',
        detail: formToPayload(formMethods.getValues()),
        skpYear: resolvedPeriodParams.skpYear,
        skpPeriod: resolvedPeriodParams.skpPeriod,
      },
      {
        onSuccess: () => {
          toast({ message: 'Berhasil simpan draf!', type: 'success' })

          handleGoToPraktikKinerjaHub()

          queryClient.invalidateQueries(API_GET_RENCANA_TINDAK_LANJUT_QUERY_KEY)
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
        },
        onError: (err: CustomError) => {
          const message = 'Gagal simpan draf. Coba lagi'
          const errorCode = getErrorCode(err)

          toast({
            message: generateErrorMessage(message, errorCode),
          })
        },
        onSettled: () => {
          handleCloseSaveDraftDialog()
        },
      }
    )
  }

  // -----------------------------------------
  // Handle kirim
  // -----------------------------------------
  const [isSubmitDialogOpen, setSubmitDialogOpen] = useState(false)

  const handleOpenSubmitDialog = () => {
    setSubmitDialogOpen(true)
  }

  const handleCloseSubmitDialog = () => {
    setSubmitDialogOpen(false)
  }

  const handleSubmit = () => {
    if (isReadOnly) return

    upsertMutation.mutate(
      {
        tipe: 'SPNF',
        state: 'SUBMITTED',
        detail: formToPayload(formMethods.getValues()),
        skpYear: resolvedPeriodParams.skpYear,
        skpPeriod: resolvedPeriodParams.skpPeriod,
      },
      {
        onSuccess: () => {
          toast({
            message: `Berhasil kirim ${stepTitle}!`,
            type: 'success',
          })

          handleGoToPraktikKinerjaHub()

          queryClient.invalidateQueries(API_GET_RENCANA_TINDAK_LANJUT_QUERY_KEY)
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
        },
        onError: (err: CustomError) => {
          const message = `Gagal kirim ${stepTitle}. Coba lagi`
          const errorCode = getErrorCode(err)

          toast({
            message: generateErrorMessage(message, errorCode),
          })
        },
        onSettled: () => {
          handleCloseSubmitDialog()
        },
      }
    )
  }

  // -----------------------------------------
  // Enable unsaved changes back navigation guard
  // -----------------------------------------
  const [isUnsavedConfirmationDialogOpen, setIsUnsavedConfirmationDialogOpen] =
    useState(false)

  useBackNavigationConfirmation({
    shouldConfirm: !isReadOnly && formMethods.formState.isDirty,
    onInterceptBack: () => setIsUnsavedConfirmationDialogOpen(true),
  })

  // -----------------------------------------
  // Handle guard back navigation with fallback route
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: praktikKinerjaHubRoute,
  })

  // -----------------------------------------
  // Other derived values
  // -----------------------------------------
  const isLoading = isRencanaTindakLanjutLoading

  return (
    <main className="pt-12 px-8 pb-8">
      <PageGuardPamongPelaksanaanStatus
        periodParams={resolvedPeriodParams}
        targetStep={STEP}
        unstartedRedirectRoute={PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}
        incorrectOrderRedirectRoute={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`}
      >
        <div className="mx-auto max-w-7xl flex justify-between items-center">
          <Header title={stepTitle} className="mb-9" onBack={handleBack} />
        </div>
        <FormProvider {...formMethods}>
          <form
            className="mx-auto max-w-5xl space-y-9"
            onSubmit={formMethods.handleSubmit(handleOpenSubmitDialog)}
          >
            <LangkahObservasiPraktikKinerjaSection
              steps={[
                {
                  title: 'Lakukan Refleksi',
                  description: 'Berdasarkan Hasil Observasi.',
                },
                {
                  title: 'Diskusi dengan Kepala SKB',
                  description: `Agar lebih mudah isi ${stepTitle}.`,
                },
                {
                  title: 'Lakukan Tindak Lanjut',
                  description: 'Untuk membantu Anda meningkatkan kinerja.',
                },
              ]}
            />

            {/* Rencana Tindak Lanjut section */}
            <SectionCard className="mb-6">
              <SectionCardHeader variant="light-blue">
                <Text as="h2" color="inherit" variant="heading-lg">
                  Rencana Tindak Lanjut
                </Text>
              </SectionCardHeader>
              <SectionCardBody className="px-9 py-6 space-y-12">
                {isLoading ? (
                  <div className="space-y-4 max-w-full lg:w-4/6">
                    <Shimmer height={32} width="100%" />
                    <Shimmer height={144} width="100%" />
                    <Shimmer height={200} width="100%" />
                    <Shimmer height={32} width="100%" />
                  </div>
                ) : (
                  <div className="space-y-4 max-w-full lg:w-4/6">
                    <div className="space-y-2">
                      <Text variant="heading-sm">
                        Bagaimana refleksi Anda tentang Praktik Kinerja Anda
                        selama Observasi Praktik Kinerja?
                      </Text>

                      <fieldset className="space-y-1">
                        <Text
                          as="label"
                          htmlFor="refleksi"
                          color="subdued"
                          aria-hidden={isReadOnly}
                        >
                          Refleksi
                        </Text>
                        {isReadOnly ? (
                          <>
                            <Text
                              className="overflow-wrap-anywhere"
                              aria-hidden="true"
                            >
                              {formMethods.getValues('selfReflection')}
                            </Text>
                            <span className="sr-only">
                              Refleksi:{' '}
                              {formMethods.getValues('selfReflection')}
                            </span>
                          </>
                        ) : (
                          <Textarea
                            {...formMethods.register('selfReflection', {
                              required: {
                                value: true,
                                message: 'Wajib diisi',
                              },
                              maxLength: {
                                value: 1000,
                                message: 'Maksimal 1000 karakter',
                              },
                            })}
                            id="refleksi"
                            maxLength={1000}
                            placeholder="Ketik di sini"
                            rows={3}
                            isInvalid={
                              !!formMethods.formState.errors['selfReflection']
                            }
                            errorMessage={
                              formMethods.formState.errors.selfReflection
                                ?.message
                            }
                          />
                        )}
                      </fieldset>
                    </div>

                    {!isReadOnly && (
                      <Collapsible
                        defaultOpen
                        open={isPanduanExpanded}
                        variant="card"
                        onToggle={handlePanduanToggle}
                      >
                        <Button
                          variant="ghost"
                          color="black"
                          type="button"
                          className="!p-4 !justify-between w-full"
                          aria-expanded={isPanduanExpanded}
                          aria-controls="panduan-pengisian-refleksi"
                          onClick={handlePanduanToggle}
                        >
                          <Text variant="heading-sm" as="span">
                            Panduan Pengisian Refleksi
                          </Text>
                          <span
                            className={`transition-transform duration-300${
                              isPanduanExpanded ? ' rotate-180' : ''
                            }`}
                          >
                            <CollapsibleIcon />
                          </span>
                        </Button>
                        <CollapsibleContent>
                          <div
                            className="p-4 space-y-5 border-t-subdued border-t"
                            id="panduan-pengisian-refleksi"
                          >
                            <div>
                              <Text variant="body-sm" color="subdued">
                                Tantangan bisa berupa salah satu kondisi berikut
                                ini:
                              </Text>
                              <ul className="list-disc list-inside pl-2">
                                <Text as="li" variant="body-sm" color="subdued">
                                  Kesulitan yang Anda hadapi
                                </Text>
                                <Text as="li" variant="body-sm" color="subdued">
                                  Aspek praktik yang ingin Anda tingkatkan
                                  efektivitasnya
                                </Text>
                                <Text as="li" variant="body-sm" color="subdued">
                                  Perubahan praktik agar lebih berdampak pada
                                  pembelajaran
                                </Text>
                              </ul>
                            </div>
                            <div>
                              <Text variant="body-sm" color="subdued">
                                Jelaskan aspek tantangan tersebut setidaknya:
                              </Text>
                              <ul className="list-disc list-inside pl-2">
                                <Text as="li" variant="body-sm" color="subdued">
                                  Perilaku atau kejadian yang menggambarkan
                                  tantangan
                                </Text>
                                <Text as="li" variant="body-sm" color="subdued">
                                  Faktor yang menyebabkan tantangan tersebut
                                </Text>
                                <Text as="li" variant="body-sm" color="subdued">
                                  Dampak dari tantangan tersebut pada
                                  pembelajaran
                                </Text>
                              </ul>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    <button
                      type="button"
                      className="flex items-center space-x-1 text-blue-70 py-3"
                      aria-haspopup={true}
                      aria-expanded={isHasilObservasiDialogOpen}
                      onClick={handleOpenHasilObservasiDialog}
                    >
                      <Text as="span" variant="action" color="inherit">
                        Cek Hasil Observasi Praktik Kinerja
                      </Text>
                      <Icon color="inherit">chevron_right</Icon>
                    </button>

                    <Dialog
                      closeOnOverlayClick={true}
                      isOpen={isHasilObservasiDialogOpen}
                      size="lg"
                      onClose={handleCloseHasilObservasiDialog}
                    >
                      <DialogOverlay />
                      <DialogContent aria-labelledby="hasil-observasi-praktik-kinerja-title">
                        <SectionCard>
                          <SectionCardHeader variant="light-blue">
                            <Text
                              as="h3"
                              color="inherit"
                              variant="heading-lg"
                              id="hasil-observasi-praktik-kinerja-title"
                            >
                              Hasil Observasi Praktik Kinerja
                            </Text>
                          </SectionCardHeader>
                          <SectionCardBody className="px-9 py-6 space-y-12">
                            <HasilObservasiPraktikKinerjaPegawaiTable
                              periodParams={resolvedPeriodParams}
                            />
                          </SectionCardBody>
                          <SectionCardFooter className="justify-end">
                            <Button
                              color="blue"
                              size="lg"
                              onClick={handleCloseHasilObservasiDialog}
                            >
                              Kembali
                            </Button>
                          </SectionCardFooter>
                        </SectionCard>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </SectionCardBody>
            </SectionCard>

            {/* Pilihan Belajar section */}
            <SectionCard className="mb-6">
              <SectionCardHeader
                variant="light-blue"
                className="flex items-center space-x-4"
              >
                <img
                  src={assetUrl(
                    '/illustrations/img-assessment-subject-book.png'
                  )}
                  width="64"
                  height="64"
                  alt=""
                />
                <Text as="h2" color="inherit" variant="heading-lg">
                  Pilihan Belajar
                </Text>
              </SectionCardHeader>
              <SectionCardBody className="px-9 py-6 space-y-12">
                {isRencanaTindakLanjutLoading ? (
                  <div className="space-y-4 max-w-full lg:w-4/6">
                    <Shimmer height={32} width="100%" />
                    <Shimmer height={144} width="100%" />

                    <Shimmer height={32} width="100%" />
                    <Shimmer height={144} width="100%" />

                    <Shimmer height={32} width="100%" />
                    <Shimmer height={144} width="100%" />

                    <Shimmer height={32} width="100%" />
                    <Shimmer height={144} width="100%" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <fieldset className="space-y-2">
                      <Text
                        variant="heading-sm"
                        id="description-follow-up-target"
                      >
                        Apa tujuan tindak lanjut yang ingin Anda lakukan untuk
                        meningkatkan kualitas Praktik Kinerja Anda?
                      </Text>
                      <div className="space-y-1">
                        <Text
                          as="label"
                          htmlFor="additionalFollowUpTarget"
                          color="subdued"
                          aria-hidden={isReadOnly}
                        >
                          Tujuan Tindak Lanjut
                        </Text>
                        {isReadOnly ? (
                          <>
                            <Text
                              className="overflow-wrap-anywhere"
                              aria-hidden="true"
                            >
                              {formMethods.getValues(
                                'additionalFollowUpTarget'
                              )}
                            </Text>
                            <span className="sr-only">
                              Tujuan Tindak Lanjut,{' '}
                              {formMethods.getValues(
                                'additionalFollowUpTarget'
                              )}
                            </span>
                          </>
                        ) : (
                          <div className="max-w-full lg:w-4/6">
                            <Textarea
                              {...formMethods.register(
                                'additionalFollowUpTarget',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: {
                                    value: 500,
                                    message: 'Maksimal 500 karakter',
                                  },
                                }
                              )}
                              id="additionalFollowUpTarget"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              rows={3}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalFollowUpTarget'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors
                                  .additionalFollowUpTarget?.message
                              }
                            />
                          </div>
                        )}
                      </div>
                    </fieldset>

                    <fieldset className="space-y-2">
                      <Text variant="heading-sm">
                        Apa upaya-upaya yang ingin Anda lakukan untuk mencapai
                        tujuan tindak lanjut tersebut?
                      </Text>
                      <div className="space-y-1">
                        <Text
                          as="label"
                          htmlFor="additionalFollowUpEffort"
                          color="subdued"
                          aria-hidden={isReadOnly}
                        >
                          Upaya Tindak Lanjut
                        </Text>
                        {isReadOnly ? (
                          <>
                            <Text
                              className="overflow-wrap-anywhere"
                              aria-hidden={true}
                            >
                              {formMethods.getValues(
                                'additionalFollowUpEffort'
                              )}
                            </Text>
                            <span className="sr-only">
                              Upaya Tindak Lanjut,{' '}
                              {formMethods.getValues(
                                'additionalFollowUpEffort'
                              )}
                            </span>
                          </>
                        ) : (
                          <div className="max-w-full lg:w-4/6">
                            <Textarea
                              {...formMethods.register(
                                'additionalFollowUpEffort',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: {
                                    value: 500,
                                    message: 'Maksimal 500 karakter',
                                  },
                                }
                              )}
                              className="max-w-full lg:w-4/6"
                              id="additionalFollowUpEffort"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              rows={3}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalFollowUpEffort'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors
                                  .additionalFollowUpEffort?.message
                              }
                            />
                          </div>
                        )}
                      </div>
                    </fieldset>

                    <fieldset className="space-y-2">
                      <Text variant="heading-sm">
                        Kapan Anda akan melakukan aksi tindak lanjut?
                      </Text>
                      <div className="space-y-1">
                        <Text
                          as="label"
                          htmlFor="additionalFollowUpPeriod"
                          color="subdued"
                          aria-hidden={isReadOnly}
                        >
                          Periode Tindak Lanjut
                        </Text>
                        {isReadOnly ? (
                          <>
                            <Text
                              className="overflow-wrap-anywhere"
                              aria-hidden="true"
                            >
                              {formMethods.getValues(
                                'additionalFollowUpPeriod'
                              )}
                            </Text>
                            <span className="sr-only">
                              Periode Tindak Lanjut,{' '}
                              {formMethods.getValues(
                                'additionalFollowUpPeriod'
                              )}
                            </span>
                          </>
                        ) : (
                          <div className="max-w-full lg:w-4/6">
                            <Input
                              {...formMethods.register(
                                'additionalFollowUpPeriod',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: {
                                    value: 250,
                                    message: 'Maksimal 250 karakter',
                                  },
                                }
                              )}
                              id="additionalFollowUpPeriod"
                              placeholder="Ketik di sini"
                              maxLength={250}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalFollowUpPeriod'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors
                                  .additionalFollowUpPeriod?.message
                              }
                            />
                          </div>
                        )}
                      </div>
                    </fieldset>

                    <DukunganTindakLanjutSelect<FormData>
                      options={PAMONG_DUKUNGAN_UPAYA_TINDAK_LANJUT}
                      isReadOnly={isReadOnly}
                      isRequired={true}
                      fieldName="additionalSupportNeed"
                    />
                  </div>
                )}
              </SectionCardBody>
            </SectionCard>

            {!isReadOnly && (
              <SectionCard className="space-x-4">
                <SectionCardBody className="flex justify-end gap-4 p-5">
                  <Button
                    type="button"
                    color="white"
                    size="lg"
                    disabled={!formMethods.formState.isDirty}
                    onClick={handleOpenSaveDraftDialog}
                  >
                    Simpan draf
                  </Button>
                  <Button type="submit" color="blue" size="lg">
                    Kirim
                  </Button>
                </SectionCardBody>
              </SectionCard>
            )}
          </form>
        </FormProvider>

        <ConfirmationDialog
          title="Simpan draf sebelum keluar?"
          description="Pilihan Anda akan disimpan dan bisa dilanjutkan nanti. Jika lewat batas waktu, draf akan otomatis dikumpulkan."
          confirmCta="Simpan dan keluar"
          cancelCta="Kembali"
          isOpen={isSaveDraftDialogOpen}
          isLoading={upsertMutation.isLoading}
          onCancel={handleCloseSaveDraftDialog}
          onClose={handleCloseSaveDraftDialog}
          onConfirm={handleSaveDraft}
        />
        <ConfirmationDialog
          title="Perubahan belum disimpan"
          description="Mohon simpan perubahan terlebih dahulu agar dapat dilanjutkan nanti."
          confirmCta="Simpan"
          cancelCta="Lanjut keluar"
          isOpen={isUnsavedConfirmationDialogOpen}
          isLoading={upsertMutation.isLoading}
          onCancel={handleGoToPraktikKinerjaHub}
          onClose={() => setIsUnsavedConfirmationDialogOpen(false)}
          onConfirm={handleSaveDraft}
        />
        <ConfirmationDialog
          title={`Kumpulkan ${stepTitle}?`}
          description="Anda dapat ubah isi dokumen jika diperlukan, selama belum dinilai oleh Kepala SKB."
          confirmCta="Konfirmasi"
          cancelCta="Kembali"
          topContent={
            <img
              src={assetUrl('/illustrations/img-publish-data.png')}
              alt=""
              height={84}
              width={84}
            />
          }
          isOpen={isSubmitDialogOpen}
          isLoading={upsertMutation.isLoading}
          onCancel={handleCloseSubmitDialog}
          onClose={handleCloseSubmitDialog}
          onConfirm={handleSubmit}
        />
      </PageGuardPamongPelaksanaanStatus>
    </main>
  )
}

function formToPayload(
  formData: FormData
): ParamsUpsertRencanaTindakLanjut['detail'] {
  return {
    selfReflection: formData.selfReflection,
    additionalFollowUpTarget: formData.additionalFollowUpTarget,
    additionalFollowUpEffort: formData.additionalFollowUpEffort,
    additionalFollowUpPeriod: formData.additionalFollowUpPeriod,
    additionalSupportNeed: formData.additionalSupportNeed,
  }
}
